import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { listMyRequests, listMyPurchases, getMySeats, clientCheckout, rebuyCheckout } from '../api';
import SeatManager from './SeatManager';
import ShareRequest from './ShareRequest';
import SessionPicker from './SessionPicker';
import LmsCard, { LmsButton } from './LmsAccess';
import { formatDate, formatInstant } from './dates';
import { goTo } from './urls';

const money = (cents) => (cents == null ? null : `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0 })}`);

// Re-checkout for a class already bought: renew a lapsed one, or add seats to a
// live one. Same Stripe path as the first purchase, so the confirmation banner
// and receipts work identically.
function RebuyControl({ rebuy, defaultSeats = 1, label, primary = false }) {
  const [open, setOpen] = useState(false);
  const [seats, setSeats] = useState(String(defaultSeats || 1));
  const seatCount = Math.max(1, parseInt(seats, 10) || 1);

  const buy = useMutation({
    mutationFn: () => rebuyCheckout(rebuy, seatCount),
    onSuccess: (res) => {
      if (!res?.checkout_url) return;
      if (!goTo(res.checkout_url)) toast.error('That checkout link looks invalid');
    },
    onError: (e) => toast.error(e?.message || 'Could not start checkout'),
  });

  if (!rebuy) return null;
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className={primary
          ? 'text-sm font-bold px-3 py-2 rounded-lg bg-orange-500 text-white'
          : 'text-[11px] text-blue-500'}
      >
        {label}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <input
        type="number" min="1" value={seats} aria-label="Seats"
        onChange={(e) => setSeats(e.target.value)}
        onBlur={() => setSeats(String(seatCount))}
        className="w-16 text-sm px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-transparent"
      />
      <button onClick={() => buy.mutate()} disabled={buy.isPending || buy.isSuccess}
        className="text-sm font-bold px-3 py-2 rounded-lg bg-orange-500 text-white disabled:opacity-40">
        {buy.isPending || buy.isSuccess ? 'Redirecting...' : 'Checkout'}
      </button>
      <button onClick={() => setOpen(false)} className="text-[11px] text-gray-500">cancel</button>
    </div>
  );
}

function Row({ r }) {
  const [seatsWanted, setSeatsWanted] = useState('1');
  const [sharing, setSharing] = useState(false);
  const [scheduling, setScheduling] = useState(false);

  const seatCount = Math.max(1, parseInt(seatsWanted, 10) || 1);
  const seatCents = r.seat_price_cents || 0;
  const devCents = r.dev_setup_fee_cents || 0;
  const total = seatCount * seatCents + devCents;

  const pay = useMutation({
    mutationFn: () => clientCheckout(r.concept_id, { seats: seatCount, return_url: window.location.href }),
    onSuccess: (res) => {
      if (!res?.checkout_url) return;
      if (!goTo(res.checkout_url)) toast.error('That checkout link looks invalid');
    },
    onError: (e) => toast.error(e?.message || 'Checkout failed'),
  });

  // Every settled purchase, newest first. Adding seats creates a SECOND purchase,
  // and rendering only the latest used to hide the earlier one's assigned seats.
  const purchases = r.purchases || [];
  const bought = purchases.length > 0;
  const live = purchases.filter((p) => !p.expired);
  const lapsed = bought && live.length === 0;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-sm">{r.title || r.need}</div>
          <div className="text-[11px] text-gray-400">
            {lapsed ? 'Expired' : r.status}
            {bought ? ` · ${purchases.reduce((n, p) => n + (p.seats || 0), 0)} seats across ${purchases.length} order${purchases.length === 1 ? '' : 's'}` : ''}
            {live[0]?.valid_until ? ` · valid until ${formatDate(live[0].valid_until)}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {r.status === 'Ready to buy' && !bought && (
            <>
              <input
                type="number" min="1" value={seatsWanted} aria-label="Seats"
                onChange={(e) => setSeatsWanted(e.target.value)}
                onBlur={() => setSeatsWanted(String(seatCount))}
                className="w-16 text-sm px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-transparent"
                title="Seats"
              />
              <button onClick={() => pay.mutate()} disabled={pay.isPending || pay.isSuccess}
                className="text-sm font-bold px-3 py-2 rounded-lg bg-orange-500 text-white disabled:opacity-40">
                {pay.isPending || pay.isSuccess ? 'Redirecting...' : `Pay ${money(total) || ''}`}
              </button>
            </>
          )}
          {lapsed && <RebuyControl rebuy={r.rebuy} defaultSeats={1} label="Renew" primary />}
          <LmsButton url={live[0]?.lms_url} label="Start the class" />
          {live[0] && r.delivery === 'live' && (
            <button onClick={() => setScheduling((s) => !s)} className="text-sm font-semibold px-3 py-2 rounded-lg border border-blue-400 text-blue-700 dark:text-blue-300">
              {scheduling ? 'Close schedule' : 'Schedule'}
            </button>
          )}
          <button onClick={() => setSharing((s) => !s)} className="text-[11px] text-blue-500">share</button>
        </div>
      </div>
      {r.status === 'Ready to buy' && !bought && seatCents > 0 && (
        <div className="text-[11px] text-gray-400 mt-1">
          {money(seatCents)}/seat{devCents > 0 ? ` + ${money(devCents)} one-time development fee` : ''}
        </div>
      )}
      {bought && !lapsed && r.rebuy && (
        <div className="mt-1"><RebuyControl rebuy={r.rebuy} defaultSeats={1} label="Add seats" /></div>
      )}
      {sharing && <ShareRequest requestId={r.id} />}
      {scheduling && r.concept_id && (
        <SessionPicker conceptId={r.concept_id} purchaseId={live[0]?.purchase_id} onClose={() => setScheduling(false)} />
      )}
      {purchases.map((p) => (
        <div key={p.purchase_id}>
          {purchases.length > 1 && (
            <div className="text-[11px] text-gray-400 mt-3">
              {p.seats} seat{p.seats === 1 ? '' : 's'} bought {formatInstant(p.purchased_at)}
              {p.expired ? ' (expired)' : ''}
            </div>
          )}
          <SeatManager purchaseId={p.purchase_id} readOnly={p.expired} />
        </div>
      ))}
    </div>
  );
}

function PurchasedRow({ p }) {
  const [scheduling, setScheduling] = useState(false);
  const lapsed = p.expired || p.status === 'expired';
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-sm">{p.class_title}</div>
          <div className="text-[11px] text-gray-400">
            {lapsed ? 'Expired' : 'Purchased'} · {p.seats} seats
            {p.valid_until ? ` · ${lapsed ? 'expired' : 'valid until'} ${formatDate(p.valid_until)}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {lapsed && <RebuyControl rebuy={p.rebuy} defaultSeats={p.seats} label="Buy again" primary />}
          <LmsButton url={p.lms_url} label="Start the class" />
          {!lapsed && p.status === 'paid' && p.delivery === 'live' && (
            <button onClick={() => setScheduling((s) => !s)} className="text-sm font-semibold px-3 py-2 rounded-lg border border-blue-400 text-blue-700 dark:text-blue-300">
              {scheduling ? 'Close schedule' : 'Schedule'}
            </button>
          )}
        </div>
      </div>
      {lapsed && !p.rebuy && (
        <p className="text-[11px] text-gray-400 mt-1">This class is no longer offered. Request it again and we will re-open it.</p>
      )}
      {!lapsed && p.rebuy && (
        <div className="mt-1"><RebuyControl rebuy={p.rebuy} defaultSeats={1} label="Add seats" /></div>
      )}
      {scheduling && <SessionPicker purchaseId={p.purchase_id} onClose={() => setScheduling(false)} />}
      <SeatManager purchaseId={p.purchase_id} readOnly={lapsed} />
    </div>
  );
}

// Classes the caller holds a seat in but may not have bought (invited attendees).
function AttendingRow({ s }) {
  const [scheduling, setScheduling] = useState(false);
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-sm">{s.class_title}</div>
          <div className="text-[11px] text-gray-400">You have a seat in this class</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <LmsButton url={s.lms_url} label="Start the class" />
          {s.live && (
            <button onClick={() => setScheduling((v) => !v)} className="text-sm font-semibold px-3 py-2 rounded-lg border border-blue-400 text-blue-700 dark:text-blue-300">
              {scheduling ? 'Close schedule' : 'Schedule'}
            </button>
          )}
        </div>
      </div>
      {scheduling && (
        <SessionPicker conceptId={s.concept_id} purchaseId={s.concept_id ? undefined : s.purchase_id}
          seatId={s.seat_id} onClose={() => setScheduling(false)} />
      )}
    </div>
  );
}

export default function MyClasses() {
  const reqs = useQuery({ queryKey: ['my-training-requests'], queryFn: listMyRequests });
  const purch = useQuery({ queryKey: ['my-training-purchases'], queryFn: listMyPurchases });
  const mine = useQuery({ queryKey: ['my-training-seats'], queryFn: getMySeats });

  if (reqs.isLoading || purch.isLoading) return <div className="text-sm text-gray-500">Loading...</div>;
  if (reqs.isError || purch.isError) {
    const msg = reqs.error?.message || purch.error?.message;
    return <div className="text-sm text-red-500">Could not load your classes{msg ? `: ${msg}` : ''}.</div>;
  }

  const requests = reqs.data?.requests || [];
  const purchases = purch.data?.purchases || [];
  const seats = mine.data?.seats || [];
  // Seats whose class is not already covered by a purchase row or a request row
  // the caller sees (avoids showing the same class twice).
  const purchaseIds = new Set(purchases.map((p) => p.purchase_id));
  const requestConceptIds = new Set(requests.map((r) => r.concept_id).filter(Boolean));
  const attending = seats.filter((s) => !purchaseIds.has(s.purchase_id) && !(s.concept_id && requestConceptIds.has(s.concept_id)));
  const empty = requests.length === 0 && purchases.length === 0 && attending.length === 0;

  return (
    <div className="space-y-4">
      <LmsCard />
      {empty && <div className="text-sm text-gray-500">You have no training classes yet.</div>}
      {attending.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-500">Classes I am attending</div>
          {attending.map((s) => <AttendingRow key={s.seat_id} s={s} />)}
        </div>
      )}
      {purchases.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-500">Purchased classes</div>
          {purchases.map((p) => <PurchasedRow key={p.purchase_id} p={p} />)}
        </div>
      )}
      {requests.length > 0 && (
        <div className="space-y-2">
          {purchases.length > 0 && <div className="text-xs font-semibold text-gray-500">Requested classes</div>}
          {requests.map((r) => <Row key={r.id} r={r} />)}
        </div>
      )}
    </div>
  );
}
